import { fetchWithAuth } from '@/lib/api'

export interface TipTapEditorHandlersDeps {
  fetchWithAuth: typeof fetchWithAuth
  setUploading: (v: boolean) => void
  setUploadError: (v: string | null) => void
  editor: any // TipTap Editor instance
  documentId?: string
  readOnly: boolean
  marked: any // marked library for parsing markdown
}

export function useTipTapEditorHandlers(deps: TipTapEditorHandlersDeps) {
  const { setUploading, setUploadError, editor, documentId, readOnly, marked } = deps

  // Image upload function
  const uploadImage = async (file: File) => {
    if (!documentId) {
      setUploadError('Document ID is required for image uploads')
      return null
    }

    if (!file.type.startsWith('image/')) {
      setUploadError('Only image files are supported')
      return null
    }

    setUploading(true)
    setUploadError(null)

    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('documentId', documentId)

      const response = await fetchWithAuth('/api/attachments/upload', {
        method: 'POST',
        body: formData,
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.details || error.error || 'Upload failed')
      }

      const result = await response.json()

      if (result.success && result.attachment) {
        return result.attachment.markdown
      } else {
        throw new Error('Invalid response from server')
      }
    } catch (error: any) {
      console.error('Image upload error:', error)
      setUploadError(error.message || 'Failed to upload image')
      return null
    } finally {
      setUploading(false)
    }
  }

  // Handle drag and drop
  const handleDrop = async (e: React.DragEvent) => {
    if (readOnly || !editor || !documentId) return

    e.preventDefault()
    const files = Array.from(e.dataTransfer.files)
    const images = files.filter(f => f.type.startsWith('image/'))

    if (images.length === 0) return

    for (const file of images) {
      const markdown = await uploadImage(file)
      if (markdown) {
        // Insert markdown at cursor position
        const markdownHtml = marked.parse(markdown) as string
        editor.commands.insertContent(markdownHtml)
      }
    }
  }

  return { uploadImage, handleDrop }
}
